// Pre-download Resume Review modal.
// Generates an AI quality report (overall score, ATS, strengths, improvements)
// and lets the user view a US-English translation side-by-side before downloading PDF.

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cvApi } from "@/lib/cv";
import { toast } from "sonner";
import { useLanguage } from "@/App";
import ResumeRenderer from "@/components/cv/ResumeRenderer";
import "@/components/cv/resume.css";
import {
  X, Wand2, Loader2, CheckCircle2, AlertTriangle, ShieldCheck, Languages,
  Download, FileText, Sparkles,
} from "lucide-react";

const ScoreBar = ({ label, value }) => (
  <div className="mb-2">
    <div className="flex justify-between text-[11px] text-slate-300 mb-1">
      <span>{label}</span><span className="font-bold">{value}/100</span>
    </div>
    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
      <div className={`h-full ${value >= 80 ? "bg-emerald-500" : value >= 60 ? "bg-yellow-500" : "bg-red-500"} transition-all`}
        style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }} />
    </div>
  </div>
);

export default function ResumeReviewModal({ resume, allTemplates, token, onClose, onDownload }) {
  const { t, language } = useLanguage();
  const [review, setReview] = useState(null);
  const [enData, setEnData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [viewMode, setViewMode] = useState("ar"); // ar | en | both

  const runReview = async () => {
    setReviewLoading(true);
    try {
      const res = await cvApi.review({ resume_data: resume.data, language: language === "ar" ? "ar" : "en" }, token);
      setReview(res);
    } catch {
      toast.error(t("فشلت المراجعة. حاول مجدداً.", "Review failed — please retry."));
    } finally { setReviewLoading(false); }
  };

  const translate = async () => {
    setTranslateLoading(true);
    try {
      const res = await cvApi.translate({ resume_data: resume.data, target_language: "en" }, token);
      const translated = res?.data || {};
      // Defensive merge: if backend dropped photo / options for any reason, copy them from the
      // original so the layout, color theme and profile picture stay intact in the EN preview.
      const merged = {
        ...translated,
        contact: {
          ...(resume.data?.contact || {}),
          ...(translated.contact || {}),
          photo: (translated.contact?.photo) || (resume.data?.contact?.photo) || "",
        },
        options: { ...(resume.data?.options || {}), ...(translated.options || {}) },
        // If the AI somehow dropped a section, fall back to the original Arabic content
        // so the EN tile is never rendered blank.
        summary:        translated.summary        || resume.data?.summary        || "",
        experience:     (translated.experience?.length     ? translated.experience     : resume.data?.experience)     || [],
        education:      (translated.education?.length      ? translated.education      : resume.data?.education)      || [],
        skills:         (translated.skills?.length         ? translated.skills         : resume.data?.skills)         || [],
        languages:      (translated.languages?.length      ? translated.languages      : resume.data?.languages)      || [],
        certifications: (translated.certifications?.length ? translated.certifications : resume.data?.certifications) || [],
        projects:       (translated.projects?.length       ? translated.projects       : resume.data?.projects)       || [],
        achievements:   (translated.achievements?.length   ? translated.achievements   : resume.data?.achievements)   || [],
      };
      setEnData(merged);
      setViewMode("both");
      toast.success(t("تمت الترجمة. يمكنك المقارنة قبل التحميل.", "Translated — compare both versions before download."));
    } catch {
      toast.error(t("فشلت الترجمة. حاول مجدداً.", "Translation failed — please retry."));
    } finally { setTranslateLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 lg:p-6 overflow-y-auto" data-testid="resume-review-modal">
      <div className="bg-slate-950 border border-yellow-500/30 rounded-3xl w-full max-w-6xl my-4 shadow-2xl shadow-black/60 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-900 to-slate-950">
          <div className="flex items-center gap-2">
            <Sparkles className="text-yellow-500" size={18} />
            <h2 className="font-bold text-white">{t("مراجعة سيرتك قبل التحميل", "Review your resume before download")}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2" data-testid="review-close-btn"><X size={18} /></button>
        </div>

        <div className="grid lg:grid-cols-[1.1fr_1.4fr] gap-0">
          {/* LEFT — Review report */}
          <div className="p-5 lg:p-6 space-y-4 lg:max-h-[78vh] overflow-y-auto">
            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={runReview} disabled={reviewLoading} className="btn-primary" data-testid="review-run-btn">
                {reviewLoading ? <><Loader2 size={14} className="animate-spin ml-2" />{t("يحلّل…", "Analyzing…")}</>
                                : <><Wand2 size={14} className="ml-2" />{t("ابدأ المراجعة الذكية", "Start AI review")}</>}
              </Button>
              <Button onClick={translate} disabled={translateLoading} variant="outline" className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10" data-testid="review-translate-btn">
                {translateLoading ? <><Loader2 size={14} className="animate-spin ml-2" />{t("يترجم…", "Translating…")}</>
                                  : <><Languages size={14} className="ml-2" />{t("ترجم إلى إنجليزية أمريكية", "Translate to US English")}</>}
              </Button>
            </div>

            {!review && !reviewLoading && (
              <div className="bg-slate-900/50 rounded-2xl border border-slate-800 p-4 text-sm text-slate-300 leading-relaxed">
                <p className="font-semibold text-white mb-2 inline-flex items-center gap-1"><ShieldCheck size={14} className="text-emerald-400" />{t("ماذا ستحصل من المراجعة الذكية؟", "What you'll get from the AI review:")}</p>
                <ul className="space-y-1 list-disc pr-5">
                  <li>{t("درجة احترافية شاملة من 100", "Overall professional score out of 100")}</li>
                  <li>{t("توافق مع أنظمة ATS", "ATS compatibility")}</li>
                  <li>{t("نقاط قوة سيرتك", "Your resume's strengths")}</li>
                  <li>{t("نقاط محددة لتحسينها", "Specific items to improve")}</li>
                  <li>{t("ما هو ناقص (شهادات، إنجازات قابلة للقياس…)", "What's missing (certifications, measurable achievements…)")}</li>
                </ul>
              </div>
            )}

            {review && (
              <div data-testid="review-result">
                {/* Overall score badge */}
                <div className="text-center bg-gradient-to-br from-yellow-500/10 to-amber-600/10 border border-yellow-500/30 rounded-2xl p-4 mb-3">
                  <p className="text-xs text-slate-400">{t("الدرجة الكلية", "Overall Score")}</p>
                  <p className={`text-5xl font-bold ${review.overall_score >= 80 ? "text-emerald-400" : review.overall_score >= 60 ? "text-yellow-400" : "text-red-400"}`} data-testid="review-overall-score">
                    {review.overall_score}<span className="text-2xl text-slate-500">/100</span>
                  </p>
                </div>

                <ScoreBar label={t("توافق ATS", "ATS Compatibility")}         value={review.ats_score} />
                <ScoreBar label={t("جودة الكتابة", "Readability")}              value={review.readability} />
                <ScoreBar label={t("جودة الملخص", "Summary Quality")}           value={review.summary_quality} />
                <ScoreBar label={t("جودة الخبرات", "Experience Quality")}       value={review.experience_quality} />

                <div className="mt-4 bg-slate-900/60 rounded-xl p-3 border border-slate-800">
                  <p className="text-sm text-slate-200 leading-relaxed">{language === "ar" ? review.verdict_ar : review.verdict_en}</p>
                </div>

                {review.strengths?.length > 0 && (
                  <div className="mt-3" data-testid="review-strengths">
                    <p className="text-sm font-semibold text-emerald-300 mb-1 inline-flex items-center gap-1"><CheckCircle2 size={14} />{t("نقاط القوة", "Strengths")}</p>
                    <ul className="space-y-1 text-sm text-slate-300 pr-5 list-disc">
                      {review.strengths.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {review.improvements?.length > 0 && (
                  <div className="mt-3" data-testid="review-improvements">
                    <p className="text-sm font-semibold text-yellow-300 mb-1 inline-flex items-center gap-1"><AlertTriangle size={14} />{t("ما يمكن تحسينه", "What to improve")}</p>
                    <ul className="space-y-1 text-sm text-slate-300 pr-5 list-disc">
                      {review.improvements.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {review.missing_sections?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-semibold text-red-300 mb-1">{t("ما هو ناقص", "Missing")}</p>
                    <ul className="space-y-1 text-sm text-slate-300 pr-5 list-disc">
                      {review.missing_sections.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT — Side-by-side preview */}
          <div className="bg-slate-900 lg:max-h-[78vh] overflow-y-auto border-t lg:border-t-0 lg:border-r border-slate-800 p-3">
            {enData && (
              <div className="flex gap-1 mb-2" data-testid="review-mode-tabs">
                <button onClick={() => setViewMode("ar")}   className={`text-[11px] px-3 py-1 rounded-full ${viewMode==="ar"   ? "bg-yellow-500 text-black" : "bg-slate-800 text-slate-300"}`} data-testid="review-tab-ar">{t("النسخة العربية", "Arabic")}</button>
                <button onClick={() => setViewMode("en")}   className={`text-[11px] px-3 py-1 rounded-full ${viewMode==="en"   ? "bg-yellow-500 text-black" : "bg-slate-800 text-slate-300"}`} data-testid="review-tab-en">{t("النسخة الإنجليزية", "English")}</button>
                <button onClick={() => setViewMode("both")} className={`text-[11px] px-3 py-1 rounded-full ${viewMode==="both" ? "bg-yellow-500 text-black" : "bg-slate-800 text-slate-300"}`} data-testid="review-tab-both">{t("الاثنين", "Side-by-side")}</button>
              </div>
            )}

            <div className={`gap-2 ${viewMode === "both" && enData ? "grid grid-cols-2" : "block"}`}>
              {(viewMode === "ar" || viewMode === "both" || !enData) && (
                <div>
                  <Badge className="bg-slate-800 text-slate-300 mb-1 text-[10px]">{t("النسخة الأصلية", "Original")}</Badge>
                  <div className="preview-frame">
                    <ResumeRenderer template={resume.template} data={resume.data} allTemplates={allTemplates} />
                  </div>
                </div>
              )}
              {enData && (viewMode === "en" || viewMode === "both") && (
                <div>
                  <Badge className="bg-emerald-500/20 text-emerald-300 mb-1 text-[10px]">{t("الإنجليزية الأمريكية", "US English")}</Badge>
                  <div className="preview-frame">
                    <ResumeRenderer template={resume.template} data={enData} allTemplates={allTemplates} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t border-slate-800 bg-slate-900/60">
          <p className="text-xs text-slate-400 flex items-center gap-1">
            <FileText size={12} className="text-yellow-500" />
            {t("ستُنزَّل النسخة الحالية المعروضة في المعاينة (US Letter 8.5×11).", "We'll download the version currently shown in the preview (US Letter 8.5×11).")}
          </p>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="outline" className="border-slate-700 text-slate-300" data-testid="review-cancel-btn">
              {t("إغلاق", "Close")}
            </Button>
            <Button onClick={() => onDownload(enData && (viewMode === "en") ? enData : null)} className="btn-primary" data-testid="review-download-btn">
              <Download size={14} className="ml-2" />{t("تحميل PDF الآن", "Download PDF now")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
